import { ArrowDownIcon } from "@/components/Icons/ArrowDownIcon";
import { DeployClusterIcon } from "@/components/Icons/DeployClusterIcon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { AnalyticsEventType } from "@/constants/analytics";
import { DEPLOY_CLUSTER_OPTIONS } from "@/constants/cluster";
import { useAuthState } from "@/store/auth";
import { useModalStore } from "@/store/modal";
import { ClusterType } from "@/types";
import { trackEvent } from "@/utils/analytics";
import { getUserName } from "@/utils/workos";
import { Fragment, useMemo, useState } from "react";
import { useLocation } from "wouter";

type CreateClusterButtonProps = {
  className?: string;
};

export const CreateClusterButton: React.FC<CreateClusterButtonProps> = ({ className = "" }) => {
  const profile = useAuthState((state) => state.profile);
  const openRequestModal = useModalStore((s) => s.openRequestModal);

  const [location, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const userName = useMemo(() => {
    return getUserName(profile) || "";
  }, [profile]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="xs" className={`gap-2 ${className}`}>
          <DeployClusterIcon className="w-[18px] shrink-0" />
          Deploy Cluster
          <ArrowDownIcon className="h-[6px] shrink-0 text-gray" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        data-testid={"CreateClusterModal_wrapper"}
        className="z-[31] mt-2 w-[270px] border-none p-0"
        align="center"
      >
        <div className="flex w-[270px] flex-auto flex-col overflow-hidden rounded-[16px] bg-white dark:border dark:border-gray-dark-200 dark:bg-black">
          <div className="flex flex-col gap-1 px-2 py-4">
            {DEPLOY_CLUSTER_OPTIONS.map((category) => {
              const { value, title, disabled, action, requestClusterType } = category;
              const isSameLocation = location === category.url;
              const isRequestAction = action === "request";

              return (
                <Fragment key={value}>
                  <a
                    data-testid={`CreateClusterModal_${value}`}
                    onClick={() => {
                      if (isRequestAction) {
                        openRequestModal(requestClusterType ?? ClusterType.BARE_METAL);
                        setIsOpen(false);
                        return;
                      }

                      if (isSameLocation) {
                        return;
                      }
                      if (disabled) {
                        window.open(
                          `mailto:business@io.net?subject=Inquiry%20about%20${category.emailTitle || category.title}%20service&body=Hi%20IO%20Team%2C%0A%0AI'm%20interested%20in%20the%20${category.emailTitle || category.title}%20service%20on%20IO%20Cloud!%0A%0ACheers%2C%0A${userName}`,
                          "_blank"
                        );
                        return;
                      }

                      setLocation(category.url!);

                      trackEvent(AnalyticsEventType.CREATE_CLUSTER_START, {
                        clusterType: value as ClusterType
                      });
                      window.dataLayer?.push({
                        event: AnalyticsEventType.CREATE_CLUSTER_START,
                        clusterType: value as ClusterType
                      });

                      setIsOpen(false);
                    }}
                    className={`group flex flex-row items-center gap-2 px-6 py-2 transition-colors ${
                      isSameLocation
                        ? `opacity-40`
                        : `cursor-pointer hover:rounded-lg hover:bg-container dark:hover:bg-gray-dark-500`
                    }`}
                  >
                    <div className={"flex w-[27px] items-center justify-center"}>
                      <category.icon className="h-[18px] text-gray" />
                    </div>
                    <div className="label-medium flex w-full items-center text-black dark:text-foreground">
                      {title}
                    </div>
                  </a>
                </Fragment>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
